import {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {FormikHelpers, FormikProps} from 'formik';
import {Popup, Switcher, Button} from '@tickchak/react-share/app';
import {XHR_LOADING_STATE, XHR_SUCCESS_STATE} from '../../../../assets/constants/constants';
import {TXHR} from '../../../../assets/constants/constansts.type';
import useShow, {TShow} from '../../../../hooks/useShow';
import KebabDots from '../../../../assets/icons/KebabDots';
import FormContext from '../../../components/formContext/FormContext';
import DetailsTab from './tabs/DetailsTab';
import PlaceholderTab from './tabs/PlaceholderTab';
import LecturersSelectPopup from './fields/LecturersSelectPopup';
import HallSelectPopup from './fields/HallSelectPopup';
import TimeSlotSelectPopup from './fields/TimeSlotSelectPopup';
import DaySelectPopup from './fields/DaySelectPopup';
import {initialValueLecture, buildValidationSchema} from './constants';
import {LectureFormValues, TabId} from './types';
import {createLecture} from '../../../../redux/timetable/timetableActions';
import {RootState} from '../../../../redux/store.type';
import {TSlot} from '../../../../redux/timetable/timetable.type';
import {combineDateTime} from '../../../../library/dates';
import {useTimetableDays} from '../../hooks/useTimetableDays';
import * as css from './ManageLecturePopup.css';

interface ManageLecturePopupProps {
    show: TShow;
}

const ManageLecturePopup: FC<ManageLecturePopupProps> = ({show}) => {
    const {t} = useTranslation('timetable');
    const [activeTab, setActiveTab] = useState<TabId>('details');
    const [isActive, setIsActive] = useState<boolean>(false);
    const lecturersShow = useShow();
    const hallShow = useShow();
    const slotShow = useShow();
    const dayShow = useShow();
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const {conferenceDays, currentDayIndex} = useTimetableDays();

    const handleSubmit = async (_changed: any, values: LectureFormValues, helpers: FormikHelpers<LectureFormValues>) => {
        const slot = slots.find((s: TSlot) => s.tsid === values.tsid);
        const day = conferenceDays[values.dayIndex];
        if(!slot || !day) return;
        helpers.setSubmitting(true);
        try {
            const {hasCasualPrice, title, link, priceCasual, dayIndex: _dayIndex, ...rest} = values;
            const lecture = await createLecture({
                ...rest,
                title: title.trim(),
                link: link.trim() || null,
                priceCasual: hasCasualPrice ? priceCasual : null,
                active: isActive,
                time: combineDateTime(day.timestamp, slot.timeStart),
            });
            if(lecture) show.close();
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const tabs = [
        {id: 'details' as const, label: t('tabDetails')},
        {id: 'tickets' as const, label: t('tabTickets')},
        {id: 'advanced' as const, label: t('tabAdvanced')},
    ];

    const headerEnd = (
        <css.headerEnd>
            <Switcher
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                label={{on: t('toggleActive'), off: t('toggleInactive'), show: true}}
                ariaLabel={t('toggleActive')}
            />
            <css.kebab type="button" aria-label="more">
                <KebabDots />
            </css.kebab>
        </css.headerEnd>
    );

    return (
        <>
            <Popup show={show} data-cy="manageLecturePop">
                <Popup.Tabs
                    tabs={tabs}
                    value={activeTab}
                    onChange={v => setActiveTab(v as TabId)}
                    hasInnerPopup={true}
                    header={
                        <Popup.Header
                            title={t('manageLecture')}
                            onClose={show.close}
                            endSlot={headerEnd}
                        />
                    }
                >
                    <FormContext
                        initialValues={{...initialValueLecture, dayIndex: currentDayIndex}}
                        validationSchema={buildValidationSchema(t('fieldRequired'))}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({submitForm, isSubmitting}: FormikProps<LectureFormValues>) => (
                            <>
                                <css.form placeholder="" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
                                    {activeTab === 'details' && <DetailsTab lecturersShow={lecturersShow} hallShow={hallShow} slotShow={slotShow} dayShow={dayShow} />}
                                    {activeTab !== 'details' && <PlaceholderTab />}
                                </css.form>
                                <css.footer>
                                    <Button variant="neutral" size="category" onClick={show.close} type="button" disabled={isSubmitting}>
                                        {t('cancel')}
                                    </Button>
                                    <Button
                                        variant="brand"
                                        size="category"
                                        onClick={submitForm}
                                        type="button"
                                        xhrState={(isSubmitting ? XHR_LOADING_STATE : XHR_SUCCESS_STATE) as TXHR}
                                    >
                                        {t('createLecture')}
                                    </Button>
                                </css.footer>
                                <LecturersSelectPopup show={lecturersShow} />
                                <HallSelectPopup show={hallShow} />
                                <TimeSlotSelectPopup show={slotShow} />
                                <DaySelectPopup show={dayShow} />
                            </>
                        )}
                    </FormContext>
                </Popup.Tabs>
            </Popup>
        </>
    );
};

export default ManageLecturePopup;
